import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { supabase } from '../../../supabaseConfig';
import classes from './AddIdCard.module.css';
import Loader from '../helpers/Loader/Loader';
import Modal from '../helpers/Modal/Modal';
import sign from '../../../components/signature.png';
import '../../../components/IdCard.css';

const AddIdCard = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasExistingCard, setHasExistingCard] = useState(false);
  const [existingCardData, setExistingCardData] = useState(null);
  const [idCardData, setIdCardData] = useState({
    studentRegNumber: '',
    name: '',
    className: '',
    sysId: '',
    session: '',
    cnic: '',
    photo: null,
    photoUrl: '',
  });

  // Fetch students list
  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const studentsCollection = collection(db, 'students');
        const studentDocs = await getDocs(studentsCollection);
        const studentsData = studentDocs.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setStudents(studentsData);
      } catch (error) {
        console.error('Error fetching students:', error);
        setErrorMessage('Failed to fetch students');
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, []);

  // Handle student selection
  const handleStudentSelect = async (student) => {
    setSelectedStudent(student);
    setIdCardData({
      studentRegNumber: student.stdRegNumber,
      name: `${student.firstName} ${student.lastName}`,
      className: '',
      sysId: '', // Don't auto-fill - admin must enter it
      session: '', // You may want to add session to student data
      cnic: student.cnic || '',
      photo: null,
      photoUrl: '',
    });
    setHasExistingCard(false);
    setExistingCardData(null);

    // Check if ID card already exists
    try {
      const idCardRef = doc(db, 'idCards', student.stdRegNumber);
      const idCardDoc = await getDoc(idCardRef);
      if (idCardDoc.exists()) {
        const existingData = idCardDoc.data();
        setHasExistingCard(true);
        setExistingCardData(existingData);
        // Pre-fill form with existing data
        setIdCardData(prev => ({
          ...prev,
          name: existingData.name || prev.name,
          className: existingData.className || prev.className,
          sysId: existingData.sysId || '',
          session: existingData.session || '',
          cnic: existingData.cnic || prev.cnic,
          photoUrl: existingData.photoUrl || '',
        }));
      } else {
        setHasExistingCard(false);
        setExistingCardData(null);
      }
    } catch (error) {
      console.error('Error checking existing ID card:', error);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setErrorMessage('File size should be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select an image file');
        return;
      }
      setIdCardData(prev => ({ ...prev, photo: file }));
      setErrorMessage('');
    }
  };

  // Upload image to Supabase
  const uploadImageToSupabase = async (file, studentRegNumber) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${studentRegNumber}_${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { data, error } = await supabase.storage
      .from('id-cards')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      // Provide helpful error message for bucket not found
      if (error.message && error.message.includes('Bucket not found')) {
        throw new Error('Storage bucket "id-cards" not found. Please create it in Supabase Storage dashboard: Storage > Create Bucket > Name: "id-cards" > Set to Public');
      }
      throw error;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('id-cards')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStudent) {
      setErrorMessage('Please select a student');
      return;
    }

    if (!idCardData.photo && !idCardData.photoUrl) {
      setErrorMessage('Please upload a photo');
      return;
    }

    setUploading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      let photoUrl = idCardData.photoUrl;

      // Upload new photo if provided
      if (idCardData.photo) {
        photoUrl = await uploadImageToSupabase(idCardData.photo, idCardData.studentRegNumber);
      }

      // Save ID card data to Firestore
      const idCardRef = doc(db, 'idCards', idCardData.studentRegNumber);
      const isUpdate = hasExistingCard;
      await setDoc(idCardRef, {
        studentRegNumber: idCardData.studentRegNumber,
        name: idCardData.name,
        className: idCardData.className,
        sysId: idCardData.sysId,
        session: idCardData.session,
        cnic: idCardData.cnic,
        photoUrl: photoUrl,
        createdAt: existingCardData?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      setSuccessMessage(isUpdate ? 'ID Card updated successfully!' : 'ID Card added successfully!');
      setHasExistingCard(true);
      setExistingCardData({
        studentRegNumber: idCardData.studentRegNumber,
        name: idCardData.name,
        className: idCardData.className,
        sysId: idCardData.sysId,
        session: idCardData.session,
        cnic: idCardData.cnic,
        photoUrl: photoUrl,
        createdAt: existingCardData?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
     } catch (error) {
       console.error('Error adding ID card:', error);
       let errorMsg = error.message || 'Failed to add ID card';
       
       // Provide more helpful error messages
       if (error.message && (error.message.includes('Bucket not found') || error.message.includes('bucket'))) {
         errorMsg = 'Storage bucket "id-cards" not found!\n\nPlease create it in Supabase:\n1. Go to Storage in Supabase dashboard\n2. Click "New bucket"\n3. Name it "id-cards"\n4. Make it Public\n5. Click "Create bucket"';
       } else if (error.message && (error.message.includes('row-level security') || error.message.includes('RLS') || error.message.includes('policy'))) {
         errorMsg = 'Storage bucket policy error!\n\nPlease configure RLS policies in Supabase:\n\nOption 1 (SQL Editor):\n1. Go to SQL Editor in Supabase\n2. Run the SQL commands shown in the console\n\nOption 2 (Dashboard):\n1. Go to Storage > Policies\n2. Click "id-cards" bucket\n3. Add policy: Allow INSERT for authenticated users\n4. Add policy: Allow SELECT for public\n5. Add policy: Allow UPDATE for authenticated users';
       }
       
       // Log SQL commands to console for easy copy-paste
       if (error.message && (error.message.includes('row-level security') || error.message.includes('RLS') || error.message.includes('policy'))) {
         console.log('\n=== COPY THESE SQL COMMANDS TO SUPABASE SQL EDITOR ===\n');
         console.log(`-- Allow public to read files
CREATE POLICY "Public Access"
ON storage.objects FOR SELECT
USING (bucket_id = 'id-cards');

-- Allow authenticated users to upload files
CREATE POLICY "Authenticated users can upload"
ON storage.objects FOR INSERT
WITH CHECK (bucket_id = 'id-cards' AND auth.role() = 'authenticated');

-- Allow authenticated users to update files
CREATE POLICY "Authenticated users can update"
ON storage.objects FOR UPDATE
USING (bucket_id = 'id-cards' AND auth.role() = 'authenticated');

-- Allow authenticated users to delete files
CREATE POLICY "Authenticated users can delete"
ON storage.objects FOR DELETE
USING (bucket_id = 'id-cards' AND auth.role() = 'authenticated');\n`);
         console.log('=== END OF SQL COMMANDS ===\n');
       }
       
       setErrorMessage(errorMsg);
     } finally {
       setUploading(false);
     }
  };

  // Handle delete ID card
  const handleDelete = async () => {
    if (!selectedStudent || !hasExistingCard) return;
    
    if (!window.confirm(`Are you sure you want to delete the ID card for ${selectedStudent.firstName} ${selectedStudent.lastName}?`)) {
      return;
    }

    setDeleting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Delete from Firestore
      const idCardRef = doc(db, 'idCards', selectedStudent.stdRegNumber);
      await deleteDoc(idCardRef);

      // Also delete the application to allow re-application
      const idCardApplicationRef = doc(db, 'idCardApplications', selectedStudent.stdRegNumber);
      await deleteDoc(idCardApplicationRef);


      // Optionally delete image from Supabase (if you want to clean up storage)
      // You can implement this if needed

      setSuccessMessage('ID Card deleted successfully! The student can now re-apply.');
      setHasExistingCard(false);
      setExistingCardData(null);
      setIdCardData(prev => ({
        ...prev,
        photoUrl: '',
        sysId: '',
        session: '',
      }));

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting ID card:', error);
      setErrorMessage(`Failed to delete ID card: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const filteredStudents = students.filter(student =>
    student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.stdRegNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={classes.container}>
      <h2 className={classes.heading}>Add Student ID Card</h2>

      {/* Search Students */}
      <div className={classes.searchSection}>
        <input
          type="text"
          className={classes.searchInput}
          placeholder="Search students by name or registration number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading && (
        <Modal>
          <Loader />
        </Modal>
      )}

      {/* Students List */}
      {!loading && (
        <div className={classes.studentsList}>
          {filteredStudents.map((student) => (
            <div
              key={student.id}
              className={`${classes.studentItem} ${selectedStudent?.id === student.id ? classes.selected : ''}`}
              onClick={() => handleStudentSelect(student)}
            >
              <div className={classes.studentInfo}>
                <strong>{student.firstName} {student.lastName}</strong>
                <span>{student.stdRegNumber}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Existing ID Card Preview */}
      {selectedStudent && hasExistingCard && existingCardData && (
        <div className={classes.existingCardSection}>
          <div className={classes.existingCardHeader}>
            <h3>Existing ID Card</h3>
            <div className={classes.cardActions}>
              <button
                type="button"
                onClick={handleDelete}
                className={classes.deleteButton}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : '🗑️ Delete Card'}
              </button>
            </div>
          </div>
          <div className={classes.cardPreview}>
            <div className="id-card horizontal" style={{ margin: '0 auto', transform: 'scale(0.8)' }}>
              <div className="header">
                <div className="logo">
                  <img src="/numl.png" alt="NUML Logo" crossOrigin="anonymous" />
                </div>
                <div className="meow">
                  <div className="header-text">
                    NATIONAL UNIVERSITY OF MODERN LANGUAGES
                  </div>
                  <div className="sub-header">Lahore Campus</div>
                </div>
              </div>
              <div className="contact">
                <div className="tel">Tel: 042-99204588</div>
                <div className="web">www.numl.edu.pk</div>
              </div>
              <div className="info">
                <div className="details">
                  <div><b>Name:</b> {existingCardData.name}</div>
                  <div><b>Class:</b> {existingCardData.className}</div>
                  <div><b>SYS ID:</b> {existingCardData.sysId}</div>
                  <div><b>Session:</b> {existingCardData.session}</div>
                  <div><b>CNIC #:</b> {existingCardData.cnic}</div>
                </div>
                <div className="photo">
                  {existingCardData.photoUrl ? (
                    <img src={existingCardData.photoUrl} alt="Student" crossOrigin="anonymous" />
                  ) : (
                    <div style={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: '#f0f0f0',
                      color: '#666'
                    }}>
                      No Photo
                    </div>
                  )}
                </div>
              </div>
              <div className="footer">
                <div className="signature">
                  <img src={sign} alt="Signature" crossOrigin="anonymous" />
                </div>
                Regional Director
              </div>
            </div>
          </div>
          <p className={classes.updateHint}>Update the form below to modify this ID card</p>
        </div>
      )}

      {/* ID Card Form */}
      {selectedStudent && (
        <form onSubmit={handleSubmit} className={classes.form}>
          <div className={classes.formSection}>
            <h3>Student Information</h3>
            
            <div className={classes.formGroup}>
              <label>Registration Number</label>
              <input
                type="text"
                value={idCardData.studentRegNumber}
                disabled
                className={classes.input}
              />
            </div>

            <div className={classes.formGroup}>
              <label>Name</label>
              <input
                type="text"
                value={idCardData.name}
                onChange={(e) => setIdCardData(prev => ({ ...prev, name: e.target.value }))}
                className={classes.input}
                required
              />
            </div>

            <div className={classes.formGroup}>
              <label>Class/Program</label>
              <input
                type="text"
                value={idCardData.className}
                onChange={(e) => setIdCardData(prev => ({ ...prev, className: e.target.value }))}
                className={classes.input}
                placeholder={"e.g., BSCS"}
                required
              />
            </div>

            <div className={classes.formGroup}>
              <label className={classes.requiredLabel}>
                SYS ID <span className={classes.requiredStar}>*</span>
              </label>
              <input
                type="text"
                value={idCardData.sysId}
                onChange={(e) => setIdCardData(prev => ({ ...prev, sysId: e.target.value }))}
                className={`${classes.input} ${classes.sysIdInput}`}
                placeholder="e.g., NUML-S24-32247"
                required
              />
              <small className={classes.helperText}>Enter the student's System ID (e.g., NUML-S24-32247)</small>
            </div>

            <div className={classes.formGroup}>
              <label>Session</label>
              <input
                type="text"
                value={idCardData.session}
                onChange={(e) => setIdCardData(prev => ({ ...prev, session: e.target.value }))}
                placeholder="e.g., Feb-2024 to Jan-2028"
                className={classes.input}
                required
              />
            </div>

            <div className={classes.formGroup}>
              <label>CNIC #</label>
              <input
                type="text"
                value={idCardData.cnic}
                onChange={(e) => setIdCardData(prev => ({ ...prev, cnic: e.target.value }))}
                className={classes.input}
                required
              />
            </div>

            <div className={classes.formGroup}>
              <label>Student Photo</label>
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <div className={classes.uploadArea}>
                <label htmlFor="photo-upload" className={classes.uploadButton}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
                  <span>{idCardData.photo ? 'Photo Selected' : 'Choose a Photo'}</span>
                </label>
                {(idCardData.photoUrl || idCardData.photo) && (
                  <div className={classes.preview}>
                    <img 
                      src={idCardData.photo ? URL.createObjectURL(idCardData.photo) : idCardData.photoUrl} 
                      alt="Student" 
                      className={classes.previewImage} 
                    />
                  </div>
                )}
              </div>
              {idCardData.photo && <span className={classes.fileName}>{idCardData.photo.name}</span>}
              {!idCardData.photo && !idCardData.photoUrl && <small className={classes.helperText}>Image must be &lt; 5MB.</small>}
            </div>
          </div>

          <button type="submit" className={classes.submitButton} disabled={uploading}>
            {uploading ? 'Uploading...' : hasExistingCard ? 'Update ID Card' : 'Save ID Card'}
          </button>
        </form>
      )}

      {successMessage && (
        <div className={classes.successMessage}>{successMessage}</div>
      )}

      {errorMessage && (
        <div className={classes.errorMessage}>{errorMessage}</div>
      )}

      {uploading && (
        <Modal>
          <Loader />
        </Modal>
      )}

      {deleting && (
        <Modal>
          <Loader />
        </Modal>
      )}
    </div>
  );
};

export default AddIdCard;
